import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { loadSpeechComponents } from '../../services/dataApi';
import { loadAlphabetCatalog, loadAlphabetLesson } from '../../services/topicLessons';
import { ReadingMaterial, ConversationMaterial, SectionMaterial, TableMaterial } from '../journeys/TopicLessonPage';
import LessonFlow from '../../components/LessonFlow';

// Renders a chapter authored with "interactive": false as one continuous
// scrollable page (all its cards stacked, no progress bar/swipe/practice
// button) instead of the card-by-card LessonFlow — for overview/reference
// chapters (e.g. alphabet lesson1) that aren't meant to be stepped through.
function StaticAlphabetPage({ items, label, backTo, onBack, backLabel }) {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const [speechComponents, setSpeechComponents] = useState(null);

  useEffect(() => {
    let active = true;
    loadSpeechComponents(nativeCode).then((data) => { if (active) setSpeechComponents(data); }).catch(() => {});
    return () => { active = false; };
  }, [nativeCode]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      <header className="border-b border-violet-100 bg-white/90 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          {onBack
            ? <button type="button" onClick={onBack} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl text-slate-400 transition hover:bg-slate-100" aria-label={backLabel}>←</button>
            : <Link to={backTo} className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl text-slate-400 transition hover:bg-slate-100" aria-label={backLabel}>←</Link>}
          <h1 className="min-w-0 flex-1 truncate text-sm font-black text-slate-900">{label}</h1>
        </div>
      </header>
      <main className="mx-auto max-w-3xl space-y-6 px-4 py-7">
        {items.map((item, index) => {
          if (item.type === 'intro' || item.type === 'recap') {
            return <ReadingMaterial key={index} item={item} imageUrl={null} audioUrl={null} speechComponents={speechComponents} ttsCode={null} t={t} />;
          }
          if (item.type === 'conversation') return <ConversationMaterial key={index} item={item} t={t} />;
          if (item.type === 'table') return <TableMaterial key={index} item={item} />;
          if (item.type === 'section') return <SectionMaterial key={index} item={item} t={t} />;
          return null;
        })}
      </main>
    </div>
  );
}

// Interactive lesson version of the alphabet reference table — an
// additional entry point (AlphabetTables' reference table stays unchanged).
// When alphabet/catalog.json lists more than one lesson, shows a picker first
// instead of diving straight into content (mirrors topic-lessons' catalog
// pattern, minus per-chapter progress tracking, which alphabet doesn't have
// yet). A single-chapter (or uncataloged) language skips the picker. Once a
// chapter is chosen, its own "interactive" flag decides whether it opens as
// the swipeable LessonFlow or the static single-page view above.
export default function AlphabetLessonPage() {
  const { t } = useTranslation();
  const { langCode } = useParams();
  const [searchParams] = useSearchParams();
  const requestedChapterId = searchParams.get('chapter');
  const { langMap } = useLangMap();
  const { languagePrefs } = useAuth();
  const nativeCode = languagePrefs?.nativeLanguage || 'en';
  const label = langMap[langCode]?.label;
  const backTo = `/learn/${langCode}?tab=alphabet`;
  const backLabel = t('learn.alphabetPronTab');

  const [catalog, setCatalog] = useState(null);
  const [catalogStatus, setCatalogStatus] = useState('loading');
  const [chapterId, setChapterId] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [lessonStatus, setLessonStatus] = useState('idle');

  useEffect(() => {
    let active = true;
    setCatalogStatus('loading');
    setCatalog(null);
    setChapterId(null);
    loadAlphabetCatalog(langCode, label)
      .then((chapters) => {
        if (!active) return;
        setCatalog(chapters);
        setCatalogStatus(chapters.length ? 'ready' : 'unavailable');
        // Deep-linked from the alphabet home page's chapter list — open it
        // directly instead of showing the picker again. Falls through to
        // the single-chapter auto-select below if the requested id doesn't
        // actually exist in this language's catalog.
        const requested = requestedChapterId && chapters.find((c) => c.id === requestedChapterId);
        if (requested) setChapterId(requested.id);
        else if (chapters.length === 1) setChapterId(chapters[0].id);
      })
      .catch(() => { if (active) setCatalogStatus('unavailable'); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langCode, label, requestedChapterId]);

  useEffect(() => {
    if (!chapterId) {
      setLesson(null);
      setLessonStatus('idle');
      return undefined;
    }
    let active = true;
    setLessonStatus('loading');
    setLesson(null);
    loadAlphabetLesson(langCode, chapterId, label, nativeCode)
      .then((loaded) => {
        if (!active) return;
        setLesson(loaded);
        setLessonStatus(loaded.items.length ? 'ready' : 'unavailable');
      })
      .catch(() => { if (active) setLessonStatus('unavailable'); });
    return () => { active = false; };
  }, [langCode, chapterId, label, nativeCode]);

  if (catalogStatus === 'loading' || lessonStatus === 'loading') {
    return <div className="page-shell"><div className="h-72 animate-pulse rounded-3xl bg-violet-100" aria-label={t('journeyPages.lessonLoading')} /></div>;
  }

  if (catalogStatus === 'unavailable' || !catalog || (chapterId && lessonStatus === 'unavailable')) {
    return (
      <div className="page-shell text-center">
        <div className="rounded-3xl border border-rose-200 bg-white p-10 shadow-sm">
          <span className="text-5xl" aria-hidden="true">📕</span>
          <h1 className="mt-4 text-2xl font-black text-slate-900">{t('journeyPages.lessonUnavailable')}</h1>
          <Link to={backTo} className="mt-5 inline-flex rounded-xl bg-violet-600 px-5 py-3 text-sm font-black text-white">{backLabel}</Link>
        </div>
      </div>
    );
  }

  if (!chapterId) {
    return (
      <div className="page-shell">
        <Link to={backTo} className="inline-flex items-center gap-2 text-sm font-black text-violet-700 hover:text-violet-900">
          <span aria-hidden="true">←</span> {backLabel}
        </Link>
        <h1 className="mt-5 text-2xl font-black text-slate-900">{label ? `${label} Alphabet` : backLabel}</h1>
        <div className="mt-6 space-y-3">
          {catalog.map((chapter) => (
            <button
              key={chapter.id}
              type="button"
              onClick={() => setChapterId(chapter.id)}
              className="flex w-full items-center gap-3 rounded-2xl border border-violet-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-violet-50 text-lg font-black text-violet-700">{chapter.chapterNumber}</span>
              <span className="text-sm font-black text-slate-900">{chapter.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (!lesson) return null;
  const onBack = catalog.length > 1 ? () => setChapterId(null) : undefined;

  if (!lesson.interactive) {
    return (
      <StaticAlphabetPage
        items={lesson.items}
        label={lesson.chapter.label}
        backTo={backTo}
        onBack={onBack}
        backLabel={backLabel}
      />
    );
  }

  return (
    <LessonFlow
      loader={() => Promise.resolve(lesson)}
      loaderDeps={[langCode, chapterId, label]}
      backTo={backTo}
      onBack={onBack}
      backLabel={backLabel}
    />
  );
}
